/**
 * E2E crypto for in-chat vault secret sharing (WebCrypto only).
 *
 * Contract: plans/25_07_2026/vault-suite/CONTRACTS.md §3.
 * - A random AES-256-GCM key K encrypts the item payload once (`ct`/`iv`).
 * - Per recipient: an EPHEMERAL P-256 pair does ECDH with the recipient's
 *   published public key → HKDF-SHA256 (empty salt, info
 *   "apiboo-vault-share-v1") → AES-256-GCM key that wraps raw K.
 * - The sender's own long-lived keypair lives in the vault as the hidden
 *   item named "__e2e_keypair__" (custom type, filtered from the vault UI by
 *   the "__" name-prefix convention); the public JWK is published to the
 *   backend so others can encrypt to this account.
 *
 * Envelope versions (`metadata.v`):
 * - `v: 1` — legacy, NO additional authenticated data. Both GCM calls bind
 *   nothing to their context, so the server can relocate a v1 ciphertext or a
 *   v1 wrap between channels, senders and recipients and it still decrypts.
 *   Still accepted on READ (real v1 payloads exist in shipped conversations);
 *   never written.
 * - `v: 2` — current. Every GCM call binds an AAD built from the envelope
 *   version, a domain tag ("content" / "wrap"), the channel, the sender and —
 *   for wraps — the recipient. Relocating a v2 blob (including relabelling it
 *   `v: 1`) fails the GCM tag, so a downgrade cannot buy an attacker anything
 *   that the v2 payload did not already give them. See chat-e2e-design.md §2.5.
 *
 * Nothing here ever touches zustand stores, localStorage, or logs with
 * secret material — decrypted payloads and private keys stay with the caller.
 */
import { invoke } from '@tauri-apps/api/core';
import { putMyE2eKey } from '../services/vaultShareService';

const KEYPAIR_ITEM_NAME = '__e2e_keypair__';
// Trust-on-first-use pin store: userId → first-seen public JWK string. Hidden
// from the vault UI by the "__" name-prefix convention (CONTRACTS.md §3).
const SEEN_KEYS_ITEM_NAME = '__e2e_seen_keys__';
const HKDF_INFO = 'apiboo-vault-share-v1';
const EC_PARAMS = { name: 'ECDH', namedCurve: 'P-256' };

/** Envelope version this module WRITES. v1 is read-only legacy. */
export const SHARE_ENVELOPE_VERSION = 2;
/** AAD field separator — US (0x1F), rejected inside any component below. */
const AAD_SEP = '\u001f';
const AAD_DOMAIN = 'apiboo-vault-share-v2';

/**
 * Outcome of a trust-on-first-use check. Three states, deliberately: callers
 * must not be able to spell "could not check" the same way as "nothing
 * changed". Anything that is not VERIFIED must block a share.
 */
export const KEY_CHECK = Object.freeze({
  VERIFIED: 'verified',
  CHANGED: 'changed',
  UNVERIFIED: 'unverified',
});

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function bytesToBase64(bytes) {
  let binary = '';
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) {
    binary += String.fromCharCode(view[i]);
  }
  return btoa(binary);
}

function base64ToBytes(b64) {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function codedError(code, message) {
  const err = new Error(message || code);
  err.code = code;
  return err;
}

/**
 * One AAD component. Empty or separator-bearing values are rejected rather than
 * coerced: an AAD whose fields can run together is an AAD that binds nothing,
 * and a missing channel/sender means the caller does not actually know the
 * context it is encrypting for.
 */
function aadPart(value, what) {
  const s = value == null ? '' : String(value);
  if (!s || s.includes(AAD_SEP)) {
    throw codedError('bad_share_context', `Missing or invalid ${what} for an E2E share`);
  }
  return s;
}

/**
 * AAD for the single content ciphertext. The recipient cannot be bound here —
 * one ciphertext serves every recipient — so the per-recipient binding lives in
 * the wrap AAD instead.
 */
function contentAad(context) {
  return textEncoder.encode(
    [
      AAD_DOMAIN,
      'content',
      aadPart(context?.channelId, 'channelId'),
      aadPart(context?.senderUserId, 'senderUserId'),
    ].join(AAD_SEP)
  );
}

/** AAD for one recipient's wrapped content key. */
function wrapAad(context, recipientUserId) {
  return textEncoder.encode(
    [
      AAD_DOMAIN,
      'wrap',
      aadPart(context?.channelId, 'channelId'),
      aadPart(context?.senderUserId, 'senderUserId'),
      aadPart(recipientUserId, 'recipientUserId'),
    ].join(AAD_SEP)
  );
}

/** ECDH(sharedSecret) → HKDF-SHA256(empty salt, fixed info) → AES-256-GCM key. */
async function deriveWrapKey(privateKey, publicKey, usages) {
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
  const hkdfKey = await crypto.subtle.importKey('raw', bits, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: textEncoder.encode(HKDF_INFO),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    usages
  );
}

async function importPublicJwk(jwk) {
  const parsed = typeof jwk === 'string' ? JSON.parse(jwk) : jwk;
  return crypto.subtle.importKey('jwk', parsed, EC_PARAMS, false, []);
}

/** Read privateJwk/publicJwk out of the keypair item's custom fields. */
function jwksFromFields(data) {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const byKey = {};
  for (const f of fields) byKey[f.k] = f.v;
  if (!byKey.privateJwk || !byKey.publicJwk) return null;
  return { privateJwk: JSON.parse(byKey.privateJwk), publicJwk: JSON.parse(byKey.publicJwk) };
}

async function loadOrCreateKeypair() {
  const envelopes = await invoke('vault_list');
  const existing = (Array.isArray(envelopes) ? envelopes : []).find(
    (it) => it?.name === KEYPAIR_ITEM_NAME
  );

  let jwks = null;
  if (existing) {
    const full = await invoke('vault_get_item', { id: existing.id });
    jwks = jwksFromFields(full?.data);
  }

  // An EXISTING item we cannot parse must never be overwritten.
  //
  // Writing a fresh keypair back to `existing.id` destroys the private half
  // permanently, and with it the ability to decrypt every secret ever shared
  // with this account. `__e2e_keypair__` SYNCS, so a corrupt copy is something
  // a hostile or merely buggy server can hand us — which turns "regenerate on
  // unparseable" into remotely-triggerable, irreversible data loss.
  //
  // Creating a keypair when there is genuinely no item is the normal path and
  // stays. The distinction is `existing`, not `jwks`.
  if (!jwks && existing) {
    throw Object.assign(
      new Error(
        'Your encryption keypair exists but could not be read. Refusing to replace it — ' +
          'overwriting would permanently destroy access to every secret shared with you. ' +
          'Check that your vault is unlocked and fully synced, then try again.'
      ),
      { code: 'keypair_unreadable' }
    );
  }

  if (!jwks) {
    const pair = await crypto.subtle.generateKey(EC_PARAMS, true, ['deriveBits']);
    const privateJwk = await crypto.subtle.exportKey('jwk', pair.privateKey);
    const publicJwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
    await invoke('vault_put_item', {
      item: {
        // No `id`: we only reach here when no item exists. Reusing an existing
        // id is what made this destructive — see the guard above.
        type: 'custom',
        name: KEYPAIR_ITEM_NAME,
        data: {
          fields: [
            { k: 'privateJwk', v: JSON.stringify(privateJwk), secret: true },
            { k: 'publicJwk', v: JSON.stringify(publicJwk), secret: false },
          ],
        },
      },
    });
    jwks = { privateJwk, publicJwk };
  }

  const privateKey = await crypto.subtle.importKey('jwk', jwks.privateJwk, EC_PARAMS, false, [
    'deriveBits',
  ]);
  return { privateKey, publicJwk: jwks.publicJwk };
}

// Keypair load/create is single-flight so two concurrent callers never race
// into creating two "__e2e_keypair__" items. A failed attempt (vault locked,
// keychain error, ...) is not cached — the next call retries.
let keypairInflight = null;
// Publish-once-per-session flag: the backend PUT is idempotent, this only
// avoids re-sending on every share/decrypt.
let publishedThisSession = false;

/**
 * The account's long-lived E2E keypair, loaded from (or created into) the
 * vault, with the public half lazily published to the backend.
 * Requires the vault to be unlocked — callers gate on vault_status first.
 *
 * @returns {Promise<{ privateKey: CryptoKey, publicJwk: object }>}
 */
export async function ensureE2eKeypair() {
  if (!keypairInflight) {
    keypairInflight = loadOrCreateKeypair().catch((e) => {
      keypairInflight = null;
      throw e;
    });
  }
  const pair = await keypairInflight;

  if (!publishedThisSession) {
    try {
      await putMyE2eKey(JSON.stringify(pair.publicJwk));
      publishedThisSession = true;
    } catch (e) {
      // Backend unreachable — decrypting locally still works; publishing
      // retries on the next call. (Public key only; nothing sensitive.)
      console.warn('[vaultShare] publishing E2E public key failed:', e?.message || e);
    }
  }
  return pair;
}

/**
 * Drop the module-level keypair cache and the publish-once flag. MUST be called
 * on vault lock and on logout so the in-memory private key does not outlive the
 * lock, and so a different account signed in during the same app session
 * re-publishes its own public key (the flag would otherwise suppress the PUT).
 */
export function resetE2eKeypairCache() {
  keypairInflight = null;
  publishedThisSession = false;
}

/**
 * Canonical fingerprint of a P-256 public JWK for TOFU pin comparison.
 * Returns '' for anything that is not a complete EC public JWK — including
 * `{}`, which the old `[kty, crv, x, y].join('.')` turned into the perfectly
 * comparable string "undefined.undefined.undefined.undefined". A '' result is
 * "cannot fingerprint" and callers must treat it as unverifiable, never skip it.
 */
function jwkFingerprint(jwk) {
  try {
    const parsed = typeof jwk === 'string' ? JSON.parse(jwk) : jwk;
    if (!parsed || typeof parsed !== 'object') return '';
    const parts = [parsed.kty, parsed.crv, parsed.x, parsed.y];
    if (parts.some((p) => typeof p !== 'string' || p === '' || p.includes('.'))) return '';
    return parts.join('.');
  } catch {
    return '';
  }
}

/** Load the pinned-keys vault item ({ id, map: { userId: jwkString } }). */
async function loadSeenKeysItem() {
  const envelopes = await invoke('vault_list');
  const existing = (Array.isArray(envelopes) ? envelopes : []).find(
    (it) => it?.name === SEEN_KEYS_ITEM_NAME
  );
  if (!existing) return { id: null, map: {} };
  const full = await invoke('vault_get_item', { id: existing.id });
  const fields = Array.isArray(full?.data?.fields) ? full.data.fields : [];
  const map = {};
  for (const f of fields) map[String(f.k)] = f.v;
  return { id: existing.id, map };
}

/** Persist the pinned-keys map back into the hidden vault item. */
async function saveSeenKeysItem(id, map) {
  const fields = Object.entries(map).map(([k, v]) => ({
    k: String(k),
    v: String(v),
    secret: false,
  }));
  await invoke('vault_put_item', {
    item: {
      ...(id ? { id } : {}),
      type: 'custom',
      name: SEEN_KEYS_ITEM_NAME,
      data: { fields },
    },
  });
}

function unverified(reason) {
  return { status: KEY_CHECK.UNVERIFIED, changed: [], firstUse: [], reason };
}

/**
 * Trust-on-first-use check for a fetched userId→publicJWK map. Pins keys seen
 * for the first time and reports userIds whose current key DIFFERS from the
 * previously pinned one (silent key substitution — the exact attack E2E must
 * defend against). Recipient keys come from the backend directory with no
 * authenticity guarantee, so this is the only defence against the directory
 * operator swapping in its own key.
 *
 * FAILS CLOSED. This used to return `{ changed: [] }` on any vault trouble —
 * i.e. "nothing changed, carry on" — which meant an attacker never had to beat
 * the check, only to break it (the pin store is a SYNCED vault item, so a
 * hostile server can serve a corrupt `__e2e_seen_keys__` and the alarm goes
 * quiet). Now "could not check" is its own outcome and callers must block on it.
 *
 * Three outcomes, never conflated:
 * - `KEY_CHECK.VERIFIED`   — pin store read; nothing changed. `firstUse` lists
 *                            recipients newly pinned (legitimate TOFU, not an
 *                            alarm).
 * - `KEY_CHECK.CHANGED`    — at least one pinned key differs. `changed` lists
 *                            them. The user must confirm.
 * - `KEY_CHECK.UNVERIFIED` — the check could not be performed. `reason` says
 *                            why. Never proceed silently.
 *
 * @param {Object<string,string>} keysByUserId userId → public JWK string|null
 * @param {{ excludeUserId?: string|number }} [opts] skip our own entry
 * @returns {Promise<{ status: string, changed: string[], firstUse: string[], reason: ?string }>}
 */
export async function verifyRecipientKeys(keysByUserId, opts = {}) {
  const excludeUserId = opts.excludeUserId;

  // The pin store lives in the vault, so a locked vault means "cannot verify",
  // not "nothing to see". Probed explicitly so the UI can offer an unlock.
  try {
    const status = await invoke('vault_status');
    if (!status?.unlocked) return unverified('vault_locked');
  } catch {
    return unverified('vault_unavailable');
  }

  let store;
  try {
    store = await loadSeenKeysItem();
  } catch {
    return unverified('pin_store_unreadable');
  }

  const map = { ...store.map };
  const changed = [];
  const firstUse = [];
  let dirty = false;
  for (const [uid, jwk] of Object.entries(keysByUserId || {})) {
    if (!jwk) continue;
    if (excludeUserId != null && String(uid) === String(excludeUserId)) continue;
    const fp = jwkFingerprint(jwk);
    // A key we cannot fingerprint is a key we cannot compare. Silently skipping
    // it (the old behaviour) is the same fail-open in miniature.
    if (!fp) return unverified('unreadable_recipient_key');
    const pinned = map[String(uid)];
    if (pinned == null) {
      map[String(uid)] = typeof jwk === 'string' ? jwk : JSON.stringify(jwk);
      firstUse.push(String(uid));
      dirty = true;
    } else if (jwkFingerprint(pinned) !== fp) {
      changed.push(String(uid));
    }
  }

  if (dirty) {
    try {
      await saveSeenKeysItem(store.id, map);
    } catch {
      // A pin we cannot persist is a change we will not detect next time, so
      // this is "could not verify" — unless a real change was already found,
      // in which case the more dangerous outcome wins and must be shown.
      if (changed.length === 0) return unverified('pin_store_unwritable');
    }
  }

  return {
    status: changed.length > 0 ? KEY_CHECK.CHANGED : KEY_CHECK.VERIFIED,
    changed,
    firstUse,
    reason: null,
  };
}

/**
 * Re-pin (explicitly trust) the given recipients' current keys — called only
 * after the user confirms a key-change warning in the share modal.
 *
 * THROWS if the pin cannot be recorded. Swallowing that (the old behaviour)
 * told the user "trusted" while recording nothing, and let the caller go on to
 * send. The caller must not send when this rejects.
 *
 * @throws {Error} with `code` "pin_failed"
 */
export async function pinRecipientKeys(keysByUserId, userIds) {
  let store;
  try {
    store = await loadSeenKeysItem();
  } catch {
    throw codedError('pin_failed', 'Could not read the pinned-keys store');
  }
  const map = { ...store.map };
  let dirty = false;
  for (const uid of userIds || []) {
    const jwk = keysByUserId?.[String(uid)] ?? keysByUserId?.[uid];
    if (!jwk) continue;
    map[String(uid)] = typeof jwk === 'string' ? jwk : JSON.stringify(jwk);
    dirty = true;
  }
  if (dirty) {
    try {
      await saveSeenKeysItem(store.id, map);
    } catch {
      throw codedError('pin_failed', 'Could not save the pinned-keys store');
    }
  }
}

/**
 * Encrypt an item payload ({ type, name, data }) for a set of recipients.
 *
 * Always writes `v: 2`. `context` is REQUIRED and throws if incomplete — there
 * is deliberately no "encrypt without context" path, because that path is v1
 * and v1 is exactly what this replaces.
 *
 * @param {object} itemPayload decrypted vault item payload
 * @param {Object<string, string|object>} recipientJwksByUserId userId → public JWK (string or object)
 * @param {{ channelId: string|number, senderUserId: string|number }} context AAD binding
 * @returns {Promise<{ v: 2, alg: string, ct: string, iv: string, keys: object }>}
 * @throws {Error} with `code` "bad_share_context"
 */
export async function encryptForRecipients(itemPayload, recipientJwksByUserId, context) {
  // Build both AAD shapes before touching any key material, so an incomplete
  // context fails before a half-encrypted envelope exists.
  const contentAd = contentAad(context);

  const contentKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: contentAd },
    contentKey,
    textEncoder.encode(JSON.stringify(itemPayload))
  );
  const rawKey = await crypto.subtle.exportKey('raw', contentKey);

  const keys = {};
  for (const [uid, jwk] of Object.entries(recipientJwksByUserId || {})) {
    if (!jwk) continue;
    const wrapAd = wrapAad(context, uid);
    const recipientPub = await importPublicJwk(jwk);
    const ephemeral = await crypto.subtle.generateKey(EC_PARAMS, false, ['deriveBits']);
    const wrapKey = await deriveWrapKey(ephemeral.privateKey, recipientPub, ['encrypt']);
    const wrapIv = crypto.getRandomValues(new Uint8Array(12));
    const wrapped = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: wrapIv, additionalData: wrapAd },
      wrapKey,
      rawKey
    );
    keys[String(uid)] = {
      epk: await crypto.subtle.exportKey('jwk', ephemeral.publicKey),
      iv: bytesToBase64(wrapIv),
      ct: bytesToBase64(new Uint8Array(wrapped)),
    };
  }

  return {
    v: SHARE_ENVELOPE_VERSION,
    alg: 'ECDH-P256+A256GCM',
    ct: bytesToBase64(new Uint8Array(ct)),
    iv: bytesToBase64(iv),
    keys,
  };
}

/**
 * Decrypt a `kind:"secret"` message's metadata with my vault keypair.
 *
 * Version handling, and why it is not a downgrade hole:
 * - `v: 2` decrypts WITH the AAD derived from `context`. `context` must be the
 *   channel this message is being RENDERED in and the sender the client is
 *   attributing it to — not a channel/sender copied out of the envelope, which
 *   the server controls and could simply restate.
 * - `v: 1` (or a missing `v`) decrypts without AAD. Real v1 payloads exist in
 *   shipped conversations and must keep opening.
 * - A v2 envelope relabelled `v: 1` does NOT become forgeable: its ciphertext
 *   was produced under an AAD, so decrypting it without one fails the GCM tag.
 *   The only thing v1 acceptance buys an attacker is the ability to relocate a
 *   genuinely-v1 blob, which was already true before v2 existed and expires
 *   with the last v1 message. `allowLegacyV1: false` refuses even that, for
 *   callers that can guarantee v2.
 * - Any other `v` is refused outright rather than guessed at.
 *
 * @param {object} metadata message metadata ({ v, alg, ct, iv, keys, ... })
 * @param {string|number} myUserId
 * @param {CryptoKey} privateKey my long-lived ECDH private key
 * @param {{ channelId: string|number, senderUserId: string|number, allowLegacyV1?: boolean }} [context]
 * @returns {Promise<{ type: string, name: string, data: object }>}
 * @throws {Error} with `code` "no_key", "unsupported_version", "legacy_rejected",
 *   "bad_share_context" or "decrypt_failed"
 */
export async function decryptShare(metadata, myUserId, privateKey, context = {}) {
  const rawVersion = metadata?.v == null ? 1 : Number(metadata.v);
  if (!Number.isInteger(rawVersion) || rawVersion < 1 || rawVersion > SHARE_ENVELOPE_VERSION) {
    throw codedError('unsupported_version', 'This secret uses an unsupported encryption version');
  }
  if (rawVersion < SHARE_ENVELOPE_VERSION && context.allowLegacyV1 === false) {
    throw codedError('legacy_rejected', 'This secret uses a legacy encryption version');
  }

  const keys = metadata?.keys || {};
  // JSON object keys are strings; the extra lookup covers callers passing a
  // numeric id and any oddly-serialized key map.
  const entry = keys[String(myUserId)] ?? keys[myUserId];
  if (!entry) throw codedError('no_key', 'This secret was not encrypted for this account');

  // Outside the try: a missing context is a caller bug, and must not be
  // laundered into "could not decrypt on this device".
  const contentAd = rawVersion >= 2 ? contentAad(context) : undefined;
  const wrapAd = rawVersion >= 2 ? wrapAad(context, myUserId) : undefined;

  try {
    const epk = await importPublicJwk(entry.epk);
    const wrapKey = await deriveWrapKey(privateKey, epk, ['decrypt']);
    const rawKey = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(entry.iv), additionalData: wrapAd },
      wrapKey,
      base64ToBytes(entry.ct)
    );
    const contentKey = await crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, [
      'decrypt',
    ]);
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(metadata.iv), additionalData: contentAd },
      contentKey,
      base64ToBytes(metadata.ct)
    );
    return JSON.parse(textDecoder.decode(plaintext));
  } catch (e) {
    throw codedError('decrypt_failed', 'Could not decrypt this secret');
  }
}
